import { DateTime } from "luxon";

export const getFormattedISODateTime = (
  isoString?: string | null,
  fallback: string = "-"
) => {
  if (!isoString) return fallback;

  const parsed = DateTime.fromISO(isoString);
  return parsed.isValid
    ? parsed.toLocaleString(DateTime.DATETIME_MED)
    : fallback;
};

export const getDisablePastDates = () => {
  return (current: any) => {
    if (!current) return false;
    const today = DateTime.now().startOf("day");
    return current.valueOf() < today.toMillis();
  };
};

export const getDisablePastTimes = (selectedDate: any, offsetMinutes = 5) => {
  return () => {
    if (!selectedDate) return {};

    const now = DateTime.now().plus({ minutes: offsetMinutes });
    const selectedDateTime = DateTime.fromMillis(selectedDate.valueOf());

    // If selected date is not today, allow all times
    if (!selectedDateTime.hasSame(now, "day")) {
      return {};
    }

    // If selected date is today, disable hours/minutes before now+offset
    const cutoffHour = now.hour;
    const cutoffMinute = now.minute;

    return {
      disabledHours: () => {
        const hours: number[] = [];
        for (let i = 0; i < cutoffHour; i++) {
          hours.push(i);
        }
        return hours;
      },
      disabledMinutes: (selectedHour: number) => {
        // If the selected hour is the cutoff hour, disable minutes before cutoff minute
        if (selectedHour === cutoffHour) {
          const minutes: number[] = [];
          for (let i = 0; i < cutoffMinute; i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      },
    };
  };
};

export const validateStartTime = (
  startTime: any,
  fromDate: any,
  startNow = false,
  offsetMinutes = 2 // to avoid submission delays
) => {
  if (startNow) return true;
  if (!startTime || !fromDate) return true;

  const now = DateTime.now();
  const selectedDateTime = DateTime.fromMillis(fromDate.valueOf()).set({
    hour: startTime.hour,
    minute: startTime.minute,
    second: 0,
    millisecond: 0,
  });

  // If selected time is within 2 minutes of now, treat it as "start now" and allow it
  const diffInMinutes = selectedDateTime.diff(now, "minutes").minutes;
  if (Math.abs(diffInMinutes) <= 2) {
    return true;
  }

  // Otherwise, enforce the offset requirement
  const cutoff = now.plus({ minutes: offsetMinutes });
  if (selectedDateTime < cutoff) {
    return `Start time cannot be in the past.`;
  }
  return true;
};

export const getDisableDatesBeforeStart = (startDate: any) => {
  return (current: any) => {
    if (!current || !startDate) return false;
    return current.valueOf() < startDate.valueOf();
  };
};

export const getDisableTimesBeforeStart = (
  startDate: any,
  startTime: any,
  endDate: any,
  startNow = false
) => {
  return () => {
    if (!startDate || !endDate) return {};

    // determine effective start date/time
    const startDateTime = startNow
      ? DateTime.now()
      : DateTime.fromMillis(startDate.valueOf()).set({
          hour: startTime?.hour,
          minute: startTime?.minute,
          second: 0,
          millisecond: 0,
        });

    const endDateTime = DateTime.fromMillis(endDate.valueOf());

    // If end date is not the same as start date, allow all times
    if (!endDateTime.hasSame(startDateTime, "day")) {
      return {};
    }

    // If end date is same as start date, disable times before start time
    const startHour = startDateTime.hour;
    const startMinute = startDateTime.minute;

    return {
      disabledHours: () => {
        const hours: number[] = [];
        for (let i = 0; i < startHour; i++) {
          hours.push(i);
        }
        return hours;
      },
      disabledMinutes: (selectedHour: number) => {
        // If the selected hour is the start hour, disable minutes before start minute
        if (selectedHour === startHour) {
          const minutes: number[] = [];
          for (let i = 0; i < startMinute; i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      },
    };
  };
};

export const validateEndDateTime = (
  endDate: any,
  endTime: any,
  fromDate: any,
  startTime: any,
  startNow = false
) => {
  if (!endDate || !endTime || !fromDate) return true;

  const startDateTime = startNow
    ? DateTime.now()
    : DateTime.fromMillis(fromDate.valueOf()).set({
        hour: startTime?.hour,
        minute: startTime?.minute,
        second: 0,
        millisecond: 0,
      });

  const endDateTime = DateTime.fromMillis(endDate.valueOf()).set({
    hour: endTime.hour,
    minute: endTime.minute,
    second: 0,
    millisecond: 0,
  });

  if (endDateTime <= startDateTime) {
    return "Must be after start date and time";
  }
  return true;
};
